/**
 * 路由表 — 所有路由在此集中定义
 * 顶层：初始化门禁（未初始化 → 仅渲染 InitPage；已初始化 → 正常路由）
 * ProtectedRoute(登录) > Layout > [页面]；管理页额外嵌套 AdminRoute(管理员)
 */
import { Routes, Route, Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Spinner } from '@fluentui/react-components'
import { ROUTES } from './routes'
import Layout from '@/components/Layout'
import ProtectedRoute from '@/components/ProtectedRoute'
import AdminRoute from '@/components/AdminRoute'
import LoginPage from '@/pages/LoginPage'
import OAuth2CallbackPage from '@/pages/OAuth2CallbackPage'
import InitPage from '@/pages/InitPage'
import SetupGuidePage from '@/pages/SetupGuidePage'
import StatsPage from '@/pages/StatsPage'
import DomainCenterPage from '@/pages/DomainCenterPage'
import QueuePage from '@/pages/QueuePage'
import BackupsPage from '@/pages/BackupsPage'
import AuditLogsPage from '@/pages/AuditLogsPage'
import ClientsPage from '@/pages/ClientsPage'
import TemplatesPage from '@/pages/TemplatesPage'
import SettingsPage from '@/pages/SettingsPage'
import MailboxPage from '@/pages/MailboxPage'
import InboundRulesPage from '@/pages/InboundRulesPage'
import ApiDocsPage from '@/pages/ApiDocsPage'
import RecycleBinPage from '@/pages/RecycleBinPage'
import AntispamSettingsPage from '@/pages/AntispamSettingsPage'
import DomainDetailPage from '@/pages/DomainDetailPage'
import UserDetailPage from '@/pages/UserDetailPage'
import AliasDetailPage from '@/pages/AliasDetailPage'
import QueueDetailPage from '@/pages/QueueDetailPage'
import BackupDetailPage from '@/pages/BackupDetailPage'
import AuditLogDetailPage from '@/pages/AuditLogDetailPage'
import ClientDetailPage from '@/pages/ClientDetailPage'
import TemplateDetailPage from '@/pages/TemplateDetailPage'
import SendLogDetailPage from '@/pages/SendLogDetailPage'
import DkimDetailPage from '@/pages/DkimDetailPage'
import { initApi } from '@/api/init'

function MainRoutes() {
  return (
    <Routes>
      {/* 登录页 / OAuth 回调独立于 Layout */}
      <Route path={ROUTES.LOGIN} element={<LoginPage />} />
      <Route path={ROUTES.OAUTH2_CALLBACK} element={<OAuth2CallbackPage />} />

      {/* 需要登录的页面统一放在 Layout 下 */}
      <Route element={<ProtectedRoute />}>
        <Route element={<Layout />}>
          {/* 默认主页：上线配置指引；统计概览独立于 /stats */}
          <Route path={ROUTES.HOME} element={<SetupGuidePage />} />
          <Route path={ROUTES.STATS} element={<StatsPage />} />
          <Route path={ROUTES.MAIL_TEST} element={<Navigate to={ROUTES.DOMAINS} replace />} />

          {/* 管理页：额外要求管理员角色 */}
          <Route element={<AdminRoute />}>
            <Route path={ROUTES.DOMAINS} element={<DomainCenterPage />} />
            {/* 原独立菜单页合并进「收件域」，旧 URL 重定向保持兼容 */}
            <Route path={ROUTES.USERS} element={<Navigate to={ROUTES.DOMAINS} replace />} />
            <Route path={ROUTES.ALIASES} element={<Navigate to={ROUTES.DOMAINS} replace />} />
            <Route path={ROUTES.DKIM} element={<Navigate to={ROUTES.DOMAINS} replace />} />
            <Route path={ROUTES.TLS} element={<Navigate to={ROUTES.DOMAINS} replace />} />
            <Route path={ROUTES.SERVER_CONFIG} element={<Navigate to={ROUTES.DOMAINS} replace />} />
            <Route path={ROUTES.ANTISPAM} element={<AntispamSettingsPage />} />
            <Route path={ROUTES.QUEUE} element={<QueuePage />} />
            <Route path={ROUTES.BACKUPS} element={<BackupsPage />} />
            <Route path={ROUTES.AUDIT_LOGS} element={<AuditLogsPage />} />
            <Route path={ROUTES.CLIENTS} element={<ClientsPage />} />
            <Route path={ROUTES.TEMPLATES} element={<TemplatesPage />} />
            <Route path={ROUTES.SEND_LOGS} element={<Navigate to={ROUTES.DOMAINS} replace />} />
            <Route path={ROUTES.SETTINGS} element={<SettingsPage />} />
            <Route path={ROUTES.MAILBOX} element={<MailboxPage />} />
            <Route path={ROUTES.INBOUND_RULES} element={<InboundRulesPage />} />
            {/* 保留与过期归档已并入「系统设置」，旧 URL 重定向保持兼容 */}
            <Route path={ROUTES.RETENTION} element={<Navigate to={ROUTES.SETTINGS} replace />} />
            <Route path={ROUTES.API_DOCS} element={<ApiDocsPage />} />
            <Route path={ROUTES.RECYCLE_BIN} element={<RecycleBinPage />} />

            {/* 详情页：列表行点击进入，编辑/删除等操作放在详情页 */}
            <Route path={ROUTES.DOMAIN_DETAIL} element={<DomainDetailPage />} />
            <Route path={ROUTES.USER_DETAIL} element={<UserDetailPage />} />
            <Route path={ROUTES.ALIAS_DETAIL} element={<AliasDetailPage />} />
            <Route path={ROUTES.QUEUE_DETAIL} element={<QueueDetailPage />} />
            <Route path={ROUTES.BACKUP_DETAIL} element={<BackupDetailPage />} />
            <Route path={ROUTES.AUDIT_LOG_DETAIL} element={<AuditLogDetailPage />} />
            <Route path={ROUTES.CLIENT_DETAIL} element={<ClientDetailPage />} />
            <Route path={ROUTES.TEMPLATE_DETAIL} element={<TemplateDetailPage />} />
            <Route path={ROUTES.SEND_LOG_DETAIL} element={<SendLogDetailPage />} />
            <Route path={ROUTES.DKIM_DETAIL} element={<DkimDetailPage />} />
          </Route>
        </Route>
      </Route>

      {/* 初始化页（独立于 Layout，未初始化时全站唯一入口） */}
      <Route path={ROUTES.INIT} element={<InitPage />} />

      {/* 未匹配路由 → 回首页 */}
      <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
    </Routes>
  )
}

export default function App() {
  // 初始化门禁：未初始化时无论访问哪个路径都只渲染 InitPage
  const { data, isLoading } = useQuery({
    queryKey: ['init-status'],
    queryFn: initApi.status,
    refetchInterval: 5000,
  })

  if (isLoading || !data) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spinner size="large" label="加载中..." />
      </div>
    )
  }

  if (!data.initialized) return <InitPage />

  return <MainRoutes />
}